import { ChangeDetectionStrategy, Component, computed, inject, ViewContainerRef } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Role } from '../../base/authentication/user';
import { UserService } from '../../services/user.service';
import { SynchronizationStatusIconComponent } from '../synchronization/status-icon/synchronization-status-icon.component';
import { ZenithComponentsModule } from '@mateco/zenith-webcomponents-angular/dist/zenith-webcomponents-angular';
import { SynchronizationOnlineIconComponent } from '../synchronization/online-icon/synchronization-online-icon.component';
import { RequestService } from '../../../service-worker/drive/request.service';
import { ModalService } from '../zenith/modal/modal.service';
import { take } from 'rxjs/operators';
import { LogoutConfirmationModalComponent } from '../zenith/modal/logout-confirmation-modal/logout-confirmation-modal.component';
import { MainRoutes } from '../../pages/main-routes.enum';
import { ImpersonationBadgeComponent } from '../impersonation-badge/impersonation-badge.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    TranslateModule,
    SynchronizationStatusIconComponent,
    ZenithComponentsModule,
    SynchronizationOnlineIconComponent,
    ImpersonationBadgeComponent
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly router = inject(Router);
  private readonly requestService = inject(RequestService);

  readonly navigationItems = computed(() => {
    const user = this.userService.user();

    return [
      user.hasHandoverRole() && {
        route: [MainRoutes.HANDOVER],
        requiredRole: Role.HANDOVER,
        label: 'header.navigation.handover'
      },
      user.hasDriverRole() && {
        route: [MainRoutes.NEW_DRIVER_APP],
        requiredRole: Role.DRIVER,
        label: 'header.navigation.tours'
      },
      user.hasDriverRole() && {
        route: [MainRoutes.SHIFT],
        requiredRole: Role.DRIVER,
        label: 'header.navigation.shift'
      },
      user.hasAdminRole() && {
        route: [MainRoutes.ADMIN],
        requiredRole: Role.ADMIN,
        label: 'header.navigation.admin'
      }
    ].filter(Boolean);
  });

  logout() {
    this.requestService
      .hasRequestsOrPhotoRequests()
      .pipe(take(1))
      .subscribe((hasUnsynchronizedData) =>
        hasUnsynchronizedData
          ? this.showLogoutConfirmationModal(this.viewContainerRef, () => this.navigateToLogout())
          : this.navigateToLogout()
      );
  }

  showLogoutConfirmationModal(viewContainerRef: ViewContainerRef, onConfirm: Function) {
    return this.modalService.showModal(viewContainerRef, LogoutConfirmationModalComponent, onConfirm);
  }

  private navigateToLogout() {
    this.router.navigate([MainRoutes.LOGOUT]);
  }
}
